/**
 * Viewer source URL construction.
 *
 * Single source-of-truth for "the URL the browser should fetch to render a
 * given Source." Replaces the three diverging URL builders in the client
 * (`screen-viewer.tsx`'s `getProxiedUrl`, the MJPG branch's raw direct
 * connection, and `use-local-source-text.ts`'s ad-hoc text proxy build).
 *
 * Decision tree:
 *   - !useProxy                      → return rawUrl as-is (LAN-direct)
 *   - useProxy AND sourceId present  → /api/proxy/service/{sourceId}
 *   - useProxy AND !sourceId         → /api/proxy/{stream|image|text}?url=…
 *
 * See ADR-0005 for rationale.
 */
import { isBrowserTrustworthyHost } from './network-allowlist.js';
import { ScreenFormat } from './types.js';
/**
 * Build the URL the browser should fetch to render the given Source.
 *
 * Pure — no side effects, no network. Returns `null` when the inputs are
 * insufficient (e.g. `useProxy=false` with no `rawUrl`), so callers can
 * surface a meaningful error instead of fetching `''`.
 */
export function buildViewerSourceUrl(input) {
    const { sourceId, rawUrl, format, useProxy, apiUrl } = input;
    if (!useProxy) {
        // LAN-direct mode. Without a raw URL we have nowhere to point.
        return rawUrl && rawUrl.length > 0 ? rawUrl : null;
    }
    // Cloud-proxy mode preferred when we have a sourceId — the service-by-id
    // endpoint handles permissions and trusted-LAN routing.
    if (sourceId && sourceId.length > 0) {
        return `${apiUrl}/api/proxy/service/${encodeURIComponent(sourceId)}`;
    }
    // Ad-hoc mode (urlParam, no sourceId). Fall back to the format-keyed
    // generic proxy endpoint, which the server will allow only for
    // LAN/trusted URLs (intentional security gate).
    if (!rawUrl || rawUrl.length === 0) {
        return null;
    }
    const proxyEndpoint = pickGenericProxyEndpoint(format);
    return `${apiUrl}/api/proxy/${proxyEndpoint}?url=${encodeURIComponent(rawUrl)}`;
}
/**
 * Ad-hoc (`?url=`) proxy endpoint per format.
 *
 * `/image` buffers the whole upstream body with `arrayBuffer()` before
 * replying; `/stream` pipes `response.body` straight through. So anything
 * unbounded MUST go to `/stream` — an ad-hoc MP4 pointed at a full lecture
 * recording would otherwise materialize hundreds of MB of server RAM per
 * viewer, with no Range support to boot.
 *
 * HLS has no working ad-hoc form at all: `/image` and `/stream` return the
 * `.m3u8` verbatim, so the player resolves the relative segment URIs against
 * the proxy URL and every segment 400s. Only the cloud path
 * (`/api/proxy/service/:id`) rewrites the playlist. `screen-viewer.tsx`
 * therefore refuses ad-hoc HLS-over-proxy up front rather than rendering a
 * player that can never load; `stream` here is just the least-bad fallback if
 * a future caller skips that guard.
 */
function pickGenericProxyEndpoint(format) {
    if (format === 'TEXT')
        return 'text';
    if (format === ScreenFormat.JPG)
        return 'image';
    // MJPG / MP4 / HLS are all unbounded bodies → pipe, never buffer.
    return 'stream';
}
/**
 * Whether an ad-hoc (`?url=`) source must be routed through the server proxy
 * because the page can't fetch it directly.
 *
 * MU feedback 2026-07-24: manually entering an `http://` LAN image
 * (`http://147.251.36.3:1881/…`) showed nothing ("Nelze načíst obraz"). The
 * web viewer is served over `https://polygraf.app`; a browser blocks (or
 * silently upgrades to a failing `https`) an `http://` sub-resource on an
 * `https` page — classic mixed-content. The fix is to proxy that hop through
 * the API origin (`/api/proxy/image?url=…`) so the browser only ever fetches
 * `https`.
 *
 * ⚠️ The tempting fix is to also require the target be on the trusted-host
 * allowlist here. Don't: that would put a SECOND copy of a security policy in
 * the browser, where it can silently drift from the server's (which is
 * env-extensible via `SOURCE_URL_ALLOWLIST`) and would then refuse to proxy a
 * host the server would happily serve. Trust is decided in exactly one place —
 * `routes/proxy.ts:isUrlAllowed`. This function only answers a *transport*
 * question: can the browser fetch this itself, or must it hand the fetch to the
 * API origin? A target the server ultimately refuses gets a 403 the UI can
 * report, which beats a silent console-only mixed-content block.
 *
 * The one host class that must NOT be proxied is loopback: browsers treat it as
 * a potentially-trustworthy origin and fetch it directly from an https page,
 * while the proxy's SSRF blocklist refuses it in every environment. Proxying
 * there would turn a working direct fetch into a guaranteed 403.
 *
 * Rules (fail-safe to `false` = keep direct):
 *   - native WebView (mobile app) fetches cleartext LAN fine → never proxy
 *   - page must be https AND source http (otherwise there's no mixed content)
 *   - loopback source → direct; the browser allows it and the proxy won't
 *   - unparseable source URL → false (the caller surfaces its own error)
 */
export function shouldProxyAdHocSource(input) {
    if (input.isNative)
        return false;
    if (input.pageProtocol !== 'https:')
        return false;
    try {
        const parsed = new URL(input.sourceUrl);
        if (parsed.protocol !== 'http:')
            return false;
        return !isBrowserTrustworthyHost(parsed.hostname);
    }
    catch {
        return false;
    }
}
/**
 * Decide the `credentials` mode for fetching a viewer source URL.
 *
 * Production splits origins (SPA at polygraf.app, API at api.polygraf.app):
 * `fetch` defaults to `credentials: 'same-origin'`, which silently drops the
 * Better Auth cookie on that cross-origin hop — an authorized viewer then
 * polls anonymously and gets 403 (UAT B1: the live transcript blanked for
 * exactly the accounts that could read the space). Same-origin dev never
 * reproduces this, so the decision is centralized and unit-tested here.
 *
 * Only the API origin gets `'include'`. LAN-direct sources (a P.Writer or
 * camera answering `Access-Control-Allow-Origin: *`) must stay `'omit'` —
 * the CORS spec forbids wildcard origins on credentialed requests, so
 * `'include'` would hard-fail fetches that work today. Relative and
 * unparseable URLs fail safe to `'omit'`.
 */
export function resolveFetchCredentials(targetUrl, apiUrl) {
    try {
        return new URL(targetUrl).origin === new URL(apiUrl).origin ? 'include' : 'omit';
    }
    catch {
        return 'omit';
    }
}
/**
 * Whether the transcript poll has a source to fetch from.
 *
 * A viewer source is either an ad-hoc `url` (the `?url=` LAN-direct param) or,
 * in live cloud mode, an explicit `fetchUrl` (the `/api/writer/:id/…` endpoint)
 * with an empty `url`. The live-cloud case — empty `url`, set `fetchUrl` — is
 * the product's core path (a cloud transcript has no `?url=`); guarding the
 * poll on `url` alone bailed there and blanked the live view. Both the poll's
 * start-effect and its fetch must agree on this predicate, so it lives here as
 * one tested source of truth (and `buildViewerSourceUrl` already resolves
 * `fetchUrl ?? url`).
 */
export function hasPollableSource(url, fetchUrl) {
    return !!url || !!fetchUrl;
}
/**
 * True when `targetUrl` is our cloud writer content API
 * (`GET /api/writer/:serviceId/text` or `…/v2`).
 *
 * Used to treat a 404 as "writer not publishing yet" rather than "source
 * unreachable" — students who open a transcript QR / live poll before the
 * writer announces should wait, not see a hard error (Ondra 10.07 residual).
 */
export function isWriterContentUrl(targetUrl, apiUrl) {
    try {
        const target = new URL(targetUrl);
        const api = new URL(apiUrl);
        if (target.origin !== api.origin)
            return false;
        return /^\/api\/writer\/[^/]+\/(text|v2)$/.test(target.pathname);
    }
    catch {
        return false;
    }
}
/**
 * True when the writer content endpoint answered "no publisher yet".
 *
 * Server returns 404 `"No active writer"` / `"No active v2 writer"` only when
 * there is neither a live registration nor stored document. Viewers must keep
 * polling and show "waiting for writer" — not paint a permanent error that
 * only clears on manual Retry.
 */
export function isWriterAwaitingPublisher(status, targetUrl, apiUrl) {
    return status === 404 && isWriterContentUrl(targetUrl, apiUrl);
}
//# sourceMappingURL=viewer-url.js.map